import type ApolloClient from 'apollo-client'

const REFRESH_DELAY_MS = 2000

/**
 * Schedules a delayed refetch of the specified Apollo queries.
 *
 * Uses internal Apollo Client queryManager API to find and refetch active
 * observable queries by name. This is necessary because Apollo 2.x doesn't
 * provide a public API to refetch queries by name outside of a mutation's
 * refetchQueries option.
 *
 * Names are matched by exact equality, exactly like a mutation's `refetchQueries`, so a typo or a
 * renamed operation is a silent permanent no-op (#22137). Outside production we warn about every
 * supplied name that matched no active query to make that failure loud. A miss isn't always a bug —
 * the query may simply not be mounted — but a name that never matches anywhere is.
 */
export function scheduleDelayedRefetch(client: ApolloClient<any>, queries: string[]): void {
  if (!queries?.length) {
    return
  }

  setTimeout(() => {
    const queryManager = (client as any).queryManager
    if (!queryManager?.queries) {
      return
    }
    const matched = new Set<string>()
    queryManager.queries.forEach(
      ({ observableQuery }: { observableQuery?: { queryName: string; refetch: () => Promise<any> } }) => {
        if (!observableQuery) {
          return
        }

        const queryName = observableQuery.queryName
        if (queries.includes(queryName)) {
          matched.add(queryName)
          observableQuery.refetch().catch((err: unknown) => {
            console.warn('[scheduleDelayedRefetch] refetch failed for query "%s":', queryName, err)
          })
        }
      }
    )

    if (process.env.NODE_ENV !== 'production') {
      const missed = queries.filter((queryName) => !matched.has(queryName))
      if (missed.length) {
        console.warn(
          '[scheduleDelayedRefetch] no active query matched: %s. Check the operation name — Apollo matches it exactly and fails silently.',
          missed.join(', ')
        )
      }
    }
  }, REFRESH_DELAY_MS)
}
